// Native Observation / Accident / News forms: the questions and validation are shared with the Cloud Functions
// (functions/src/nativeForms), the writes go through the submit* callables. `npm run dev` does not call them (the
// mock user has no ID token): it logs the payload and pretends to save.

import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase-config";
import type { AccidentInput, NewsInput, ObservationInput } from "../../../preschool.bee-happy.club_functions/functions/src/nativeForms/nativeForms";

export {
  ACCIDENT_CATEGORIES,
  CONCENTRATION_OPTIONS,
  FAMILIARITY_OPTIONS,
  OBSERVATION_CATEGORIES,
  TEACHER_IDS,
  validateAccident,
  validateNews,
  validateObservation,
  type AccidentInput,
  type FieldErrors,
  type NewsInput,
  type ObservationInput,
} from "../../../preschool.bee-happy.club_functions/functions/src/nativeForms/nativeForms";

const DEV = import.meta.env.DEV;

/** The Google Forms these replace; kept as a fallback until the owner switches over. */
export const OLD_FORM_URLS = {
  observation: "https://docs.google.com/forms/d/e/1FAIpQLSfEPBS79cvZt0VDJHLqjB6s44W19WOHWl9Q4HOyeOgKpFYpeg/viewform",
  accident: "https://docs.google.com/forms/d/e/1FAIpQLSfWjHE_TB50egF0Lg5c7kuC-K-TwLxhyY9Xg19o5J0HvceWDw/viewform",
  news: "https://docs.google.com/forms/d/e/1FAIpQLSf38bycyfFBHzGX_TQkFS7u4zHD--KSTf7HsUQISAf_jLBD_w/viewform",
};

export interface SubmitResult { id: string }

const send = async <T>(name: string, input: T): Promise<SubmitResult> => {
  if (DEV) {
    console.log(`[dev] ${name} would be saved (nothing is written in dev):`, input);
    await new Promise((resolve) => setTimeout(resolve, 500));
    return { id: "native_dev" };
  }
  return (await httpsCallable<T, SubmitResult>(functions, name)(input)).data;
};

export const submitObservation = (input: ObservationInput) => send("submitObservation", input);
export const submitAccident = (input: AccidentInput) => send("submitAccident", input);
export const submitNews = (input: NewsInput) => send("submitNews", input);

/** Today as YYYY-MM-DD in the browser's local time. */
export const todayYmd = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** Message of a failed callable: the validation text the function sent, or a generic one. */
export const submitErrorMessage = (error: unknown): string => {
  const e = error as { code?: string; message?: string };
  if (e?.code === "functions/permission-denied") return "You are not allowed to submit this form.";
  if (e?.code === "functions/invalid-argument" && e.message) return e.message;
  return "Could not save. Check your connection and try again.";
};
